import { Helmet } from "react-helmet-async";

const SITE_NAME = "Mahra";
const SITE_URL = "https://www.shopmahra.com.au";
const DEFAULT_DESCRIPTION = "Rent or hire premium Indian outfits in Melbourne. Lehengas, sarees and accessories available with convenient delivery and flexible rental options.";
const DEFAULT_IMAGE = SITE_URL + "/images/og-image.png";

/**
 * SEO component — adds meta tags, Open Graph, Twitter Cards, canonical URL, and JSON-LD.
 * Drop into any page component. No visual output.
 */
export default function SEO({
  title,
  rawTitle,
  description = DEFAULT_DESCRIPTION,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  jsonLd,
  product,
  breadcrumbs,
}) {
  const fullTitle = rawTitle || (title ? String(title) + " | " + SITE_NAME : "Mahra – Premium Indian Outfit Rental & Hire Melbourne");
  const canonicalUrl = SITE_URL + path;
  const imageUrl = image && image.startsWith("http") ? image : SITE_URL + (image || "");

  // Collect all JSON-LD blocks to render
  const jsonLdBlocks = [];

  // Custom JSON-LD (can be a single object or array of objects)
  if (jsonLd) {
    if (Array.isArray(jsonLd)) {
      jsonLd.forEach((item) => { if (item) jsonLdBlocks.push(item); });
    } else {
      jsonLdBlocks.push(jsonLd);
    }
  }

  // Product JSON-LD
  if (product) {
    const productImages = (product.images && product.images.length > 0) ? product.images : [DEFAULT_IMAGE];
    jsonLdBlocks.push({
      "@context": "https://schema.org",
      "@type": "Product",
      name: `${product.name} Rental Melbourne – Indian Outfit Hire`,
      image: productImages,
      description: product.description || description,
      brand: { "@type": "Brand", name: "Mahra" },
      offers: {
        "@type": "Offer",
        priceCurrency: "AUD",
        price: product.price,
        availability: product.available !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        url: canonicalUrl,
        itemCondition: "https://schema.org/UsedCondition",
        businessFunction: "http://purl.org/goodrelations/v1#LeaseOut",
      },
      ...(product.category ? { category: product.category } : {}),
    });
  }

  // Breadcrumb JSON-LD
  if (breadcrumbs && breadcrumbs.length > 0) {
    jsonLdBlocks.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((bc, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: bc.name,
        ...(bc.url ? { item: SITE_URL + bc.url } : {}),
      })),
    });
  }

  return (
    <>
      <Helmet title={fullTitle}>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:type" content={type} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_AU" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        {product ? <meta property="product:price:amount" content={String(product.price)} /> : null}
        {product ? <meta property="product:price:currency" content="AUD" /> : null}
      </Helmet>
      {jsonLdBlocks.map((block, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }}
        />
      ))}
    </>
  );
}

/**
 * LocalBusiness JSON-LD for Mahra
 */
export const LOCAL_BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Mahra",
  description: "Premium Indian outfit rental and hire in Melbourne. Lehengas, sarees, anarkalis and accessories for weddings, engagements and cultural events.",
  url: SITE_URL,
  address: {
    "@type": "PostalAddress",
    addressLocality: "Oakleigh South",
    addressRegion: "VIC",
    postalCode: "3167",
    addressCountry: "AU",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: -37.9167,
    longitude: 145.0833,
  },
  areaServed: {
    "@type": "City",
    name: "Melbourne",
    containedInPlace: { "@type": "State", name: "Victoria" },
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Indian Outfit Rental Collection",
    itemListElement: [
      { "@type": "OfferCatalog", name: "Lehengas for Hire" },
      { "@type": "OfferCatalog", name: "Sarees for Rent" },
      { "@type": "OfferCatalog", name: "Anarkalis & Shararas" },
      { "@type": "OfferCatalog", name: "Indo-Western Outfits" },
      { "@type": "OfferCatalog", name: "Jewellery & Accessories" },
    ],
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    opens: "09:00",
    closes: "18:00",
  },
  priceRange: "$$",
  image: DEFAULT_IMAGE,
};

/**
 * Organization JSON-LD
 */
export const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Mahra",
  url: SITE_URL,
  logo: SITE_URL + "/images/logo.png",
  description: "Premium Indian outfit rental and hire service based in Melbourne, Australia.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Oakleigh South",
    addressRegion: "VIC",
    postalCode: "3167",
    addressCountry: "AU",
  },
};
